/**
 * "Resources" section on the parent /circle page. Renders the curated
 * resources matched to this family (GET /api/circle/resources), grouped by
 * category. Read-only except the per-entry "This didn't work" report
 * (w1023c L3). Outside groups show only here; TRF groups also show on
 * Community.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LifeBuoy } from 'lucide-react';
import { api } from '@/services/api';
import { FamilyResourceList } from '@/components/circle/FamilyResourceList';
import type { Resource } from '@/types/api';

export function ResourcesSection() {
  const { t } = useTranslation('circle');
  const { data, isLoading } = useQuery({
    queryKey: ['support-resources'],
    queryFn: () => api.circle.getResources(),
  });
  const resources: Resource[] = data?.data ?? [];

  if (isLoading) return null;
  if (resources.length === 0) return null; // stay quiet when nothing is curated for this family

  return (
    <section className="space-y-4 pt-4">
      <div className="flex items-center gap-2">
        <LifeBuoy className="h-5 w-5 text-stevens-indigo" />
        <h2 className="font-serif text-2xl font-medium tracking-tight text-foreground">
          {t('resources.title')}
        </h2>
      </div>
      <p className="text-body text-stevens-ink-3">{t('resources.intro')}</p>

      {/* The parent page is the one surface that may report an entry (w1023c L3). */}
      <FamilyResourceList resources={resources} reportable />
    </section>
  );
}
